"use client";

import { MarginAssetMode } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { isPerpsExchange } from "./config";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

/**
 * Futures collateral model of the account ("single" vs "multi"), from the dedicated endpoint.
 * Binance is resolved from its Multi-Assets Mode server-side; other venues resolve statically.
 * Rarely changes, so it's cached aggressively. Consumers treat `undefined` as "single".
 */
export function useCexPerpsMarginAssetMode(exchangeId: string, accountId: string | undefined) {
  const { auth } = useHubUser();
  const apiHelper = useApi();

  const { data } = useQuery({
    queryKey: ["cex-perps-margin-asset-mode", exchangeId, accountId],
    queryFn: async (): Promise<MarginAssetMode> => {
      const { assetMode } = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_MARGIN_ASSET_MODE, {
        queryParams: { accountId: accountId!.toString() },
      });
      return assetMode;
    },
    enabled: isPerpsExchange(exchangeId) && !!accountId && !!auth.identityToken,
    // Always refetch on mount so a page refresh reflects the account's current asset mode, ignoring staleTime.
    refetchOnMount: "always",
    staleTime: 60 * 60_000,
    refetchInterval: 60 * 60_000,
  });

  return data;
}
